import { useState } from "react"
import { Link } from "react-router"
import { SUPPORT_EMAIL } from "@shared/contact"
import {
  Field,
  FormCard,
  FormError,
  PageHero,
  CustomSelectField,
  SubmitButton,
  SuccessCard,
  TextAreaField,
  usePublicForm,
} from "./components"

const INQUIRY_TYPES = [
  { label: "Sponsorship", value: "sponsorship" },
  { label: "Corporate partnership", value: "corporate-partnership" },
  { label: "School partnership", value: "school-partnership" },
  { label: "Media or press", value: "media" },
  { label: "Something else", value: "other" },
] as const

function ContactIcon({ type }: { type: "email" | "location" }) {
  return (
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-forest-950">
      {type === "location" ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M7 18C5.17107 18.4117 4 19.0443 4 19.7537C4 20.9943 7.58172 22 12 22C16.4183 22 20 20.9943 20 19.7537C20 19.0443 18.8289 18.4117 17 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14.5 9C14.5 10.3807 13.3807 11.5 12 11.5C10.6193 11.5 9.5 10.3807 9.5 9C9.5 7.61929 10.6193 6.5 12 6.5C13.3807 6.5 14.5 7.61929 14.5 9Z" stroke="white" strokeWidth="1.5" />
          <path d="M13.2574 17.4936C12.9201 17.8184 12.4693 18 12.0002 18C11.531 18 11.0802 17.8184 10.7429 17.4936C7.6543 14.5008 3.51519 11.1575 5.53371 6.30373C6.6251 3.67932 9.24494 2 12.0002 2C14.7554 2 17.3752 3.67933 18.4666 6.30373C20.4826 11.1514 16.3536 14.5111 13.2574 17.4936Z" stroke="white" strokeWidth="1.5" />
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M2 6L8.91302 9.91697C11.4616 11.361 12.5384 11.361 15.087 9.91697L22 6" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M2.01577 13.4756C2.08114 16.5412 2.11383 18.0739 3.24496 19.2094C4.37608 20.3448 5.95033 20.3843 9.09883 20.4634C11.0393 20.5122 12.9607 20.5122 14.9012 20.4634C18.0497 20.3843 19.6239 20.3448 20.7551 19.2094C21.8862 18.0739 21.9189 16.5412 21.9842 13.4756C22.0053 12.4899 22.0053 11.5101 21.9842 10.5244C21.9189 7.45886 21.8862 5.92609 20.7551 4.79066C19.6239 3.65523 18.0497 3.61568 14.9012 3.53657C12.9607 3.48781 11.0393 3.48781 9.09882 3.53656C5.95033 3.61566 4.37608 3.65521 3.24495 4.79065C2.11382 5.92608 2.08114 7.45885 2.01576 10.5244C1.99474 11.5101 1.99475 12.4899 2.01577 13.4756Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

export function ContactPage() {
  const { status, submit, reset } = usePublicForm("/api/public/inquiries")
  const [confirmedReset, setConfirmedReset] = useState(0)

  if (status === "success") {
    return (
      <section className="bg-cream-50 py-20 lg:py-24">
        <div className="public-container">
          <SuccessCard
            eyebrow="Inquiry received"
            title="Thank you for reaching out."
            message="Your inquiry is in. Our team will reach out shortly."
            onReset={() => {
              setConfirmedReset((n) => n + 1)
              reset()
            }}
          />
        </div>
      </section>
    )
  }

  return (
    <>
      <PageHero
        eyebrow="Contact"
        eyebrowDot
        className="contact-hero"
        title="Let's build this together."
        lede="Every partnership begins with a formal inquiry — a small commitment that helps us focus our energy where it will matter most."
      />

      <section className="contact-content-section bg-cream-50 pb-10 pt-10 lg:pb-0 lg:pt-[60px]">
        <div className="public-container grid items-start gap-10 lg:grid-cols-[minmax(0,550px)_minmax(0,1fr)] lg:gap-[39px]">
          <div className="contact-details order-2 lg:order-1">
            <article className="contact-method">
              <ContactIcon type="email" />
              <div className="contact-method__copy">
                <h3 className="text-xl font-semibold text-ink">Email</h3>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-lg text-ink transition-colors hover:text-forest-950">
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </article>
            <article className="contact-method">
              <ContactIcon type="location" />
              <div className="contact-method__copy">
                <h3 className="text-xl font-semibold text-ink">Base</h3>
                <p className="text-lg text-ink">Port Harcourt, Rivers State, Nigeria</p>
              </div>
            </article>
            <div className="contact-routing-note">
              <p>
                For nominations use the{" "}
                <Link to="/nominate" className="font-semibold text-forest-950 underline decoration-gold-500 decoration-2 underline-offset-4">
                  nomination form
                </Link>
                . For school partnerships use the{" "}
                <Link to="/partner-schools#apply" className="font-semibold text-forest-950 underline decoration-gold-500 decoration-2 underline-offset-4">
                  school application
                </Link>
                .
              </p>
            </div>
          </div>

          <FormCard
            className="contact-form-card order-1 lg:order-2"
            title="Partner or sponsor inquiry"
            subtitle="Share a few details and our team will reach out to walk you through matching, funding, and updates."
          >
            <form
              key={confirmedReset}
              onSubmit={(event) => {
                const form = new FormData(event.currentTarget)
                const typeLabel = String(form.get("type") ?? "Something else")
                const type = INQUIRY_TYPES.find((t) => t.label === typeLabel)?.value ?? "other"
                submit(event, {
                  name: String(form.get("name") ?? ""),
                  email: String(form.get("email") ?? ""),
                  phone: String(form.get("phone") ?? "") || undefined,
                  organization: String(form.get("organization") ?? "") || undefined,
                  type,
                  message: String(form.get("message") ?? ""),
                })
              }}
              className="contact-form"
            >
              <Field label="Full name" name="name" required placeholder="Your name" disabled={status === "loading"} />
              <Field
                label={<><span>Organization</span> <span className="text-ink/50">(Optional)</span></>}
                name="organization"
                placeholder="Organization name"
                disabled={status === "loading"}
              />
              <div className="grid grid-cols-2 gap-3 lg:gap-[19px]">
                <Field label="Email" name="email" required type="email" placeholder="you@example.com" disabled={status === "loading"} />
                <Field label="Phone Number" name="phone" type="tel" placeholder="+234..." disabled={status === "loading"} />
              </div>
              <CustomSelectField
                label="I’m reaching out about?"
                name="type"
                options={INQUIRY_TYPES.map((t) => t.label)}
                disabled={status === "loading"}
              />
              <TextAreaField
                label="Tell us about your interests"
                name="message"
                required
                placeholder="A few sentences on what you’d like to explore..."
                disabled={status === "loading"}
              />
              <div className="contact-form__actions">
                <SubmitButton loading={status === "loading"} withArrow={false}>Send inquiry</SubmitButton>
                <p>We treat every inquiry as a formal commitment to explore sponsorship, not a mailing list signup.</p>
                {status === "error" && <FormError />}
              </div>
            </form>
          </FormCard>
        </div>
      </section>
    </>
  )
}
